// ======================================================================
// FILE:
// src/modules/service_crm/reports/pages/ServiceTicketReportPage.js
//
// MODULE:
// Service CRM
//
// FEATURE:
// Service Ticket Report
//
// PHASE 1
// - Ticket Report
// - Search
// - Pagination
// - Filters
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import reportService
from "../services/reportService";

export default function ServiceTicketReportPage() {

  // =========================================================
  // STATE
  // =========================================================

  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [total, setTotal] =
    useState(0);

  // =========================================================
  // FILTERS
  // =========================================================

  const [filters, setFilters] =
  useState({

    from_date: "",

    to_date: "",

    ticket_no: "",

    customer_name: "",

    instance_id: "",

    app_code: "",

    engine_serial_no: "",

    assigned_engineer: "ALL",

    status: "ALL",

    priority: "ALL",

    category: "ALL",

    state: "ALL",

    city: "ALL",

    feedback_completed: "ALL",

    page: 1,

    page_size: 20,

  });

  // =========================================================
  // LOAD
  // =========================================================

  useEffect(() => {

    loadData();

  }, [filters.page]);

  // =========================================================
  // LOAD DATA
  // =========================================================

  async function loadData() {

    try {

      setLoading(true);

      const data =
        await reportService
          .getTicketReport(
            filters
          );

      setRows(
        data?.rows || []
      );

      setTotal(
        data?.total || 0
      );

    } catch (err) {

      console.error(
        err
      );

      alert(
        "Failed to load report"
      );

    } finally {

      setLoading(false);

    }

  }
  // =========================================================
// EXPORT EXCEL
// =========================================================

async function handleExportExcel() {

  try {

    const blob =
      await reportService
        .exportTicketReportExcel(
          filters
        );

    const url =
      window.URL.createObjectURL(
        new Blob([blob])
      );

    const link =
      document.createElement("a");

    link.href = url;

    link.setAttribute(
      "download",
      "service_ticket_report.xlsx"
    );

    document.body.appendChild(
      link
    );

    link.click();

    link.remove();

  } catch (err) {

    console.error(
      err
    );

    alert(
      "Failed to export Excel report"
    );

  }

}

  // =========================================================
  // FILTER CHANGE
  // =========================================================

  function handleFilterChange(
    e
  ) {

    setFilters({

      ...filters,

      [e.target.name]:
        e.target.value,

    });

  }

  // =========================================================
  // SEARCH
  // =========================================================

  function handleSearch() {

    setFilters({

      ...filters,

      page: 1,

    });

    loadData();

  }

  // =========================================================
  // RESET
  // =========================================================

  function handleReset() {

    setFilters({

       from_date: "",

    to_date: "",

    ticket_no: "",

    customer_name: "",

    instance_id: "",

    app_code: "",

    engine_serial_no: "",

    assigned_engineer: "ALL",

    status: "ALL",

    priority: "ALL",

    category: "ALL",

    state: "ALL",

    city: "ALL",

    feedback_completed: "ALL",

    page: 1,

    page_size: 20,

    });

  }

  // =========================================================
  // STATUS BADGE
  // =========================================================

  function renderStatus(status) {

    const value =
      (status || "").toUpperCase();

    let bg = "#6c757d";

    if (value === "OPEN") {

      bg = "#dc3545";

    }

    else if (
      value === "ASSIGNED"
    ) {

      bg = "#fd7e14";

    }

    else if (
      value === "IN_PROGRESS"
    ) {

      bg = "#0d6efd";

    }

    else if (
      value === "RESOLVED"
    ) {

      bg = "#198754";

    }

    else if (
      value === "CLOSED"
    ) {

      bg = "#212529";

    }

    return (

      <span
        style={{

          background: bg,

          color: "#fff",

          padding:
            "5px 10px",

          borderRadius: 20,

          fontSize: 12,

          fontWeight: 600,

        }}
      >
        {status}
      </span>

    );

  }

  // =========================================================
  // RENDER
  // =========================================================

  return (

    <div>

      {/* ===================================================== */}
      {/* HEADER */}
      {/* ===================================================== */}

      <div
        style={{
          marginBottom: 20,
        }}
      >

        <h2
          style={{
            margin: 0,
          }}
        >
          Service Ticket Report
        </h2>

        <div
          style={{
            color: "#6c757d",
          }}
        >
          Service ticket reporting and analytics
        </div>

      </div>

      {/* ===================================================== */}
      {/* FILTERS */}
      {/* ===================================================== */}

      <div
        style={{

          background: "#fff",

          borderRadius: 14,

          padding: 16,

          marginBottom: 20,

          boxShadow:
            "0 2px 8px rgba(0,0,0,0.06)",

        }}
      >

        <div
            style={{

                display: "grid",

                gridTemplateColumns:
                "repeat(auto-fit, minmax(220px, 1fr))",

                gap: 14,

            }}
            >

            <input
                type="date"
                className="form-control"
                name="from_date"
                value={filters.from_date}
                onChange={handleFilterChange}
            />

            <input
                type="date"
                className="form-control"
                name="to_date"
                value={filters.to_date}
                onChange={handleFilterChange}
            />

            <input
                type="text"
                className="form-control"
                placeholder="Ticket No"
                name="ticket_no"
                value={filters.ticket_no}
                onChange={handleFilterChange}
            />

            <input
                type="text"
                className="form-control"
                placeholder="Customer Name"
                name="customer_name"
                value={filters.customer_name}
                onChange={handleFilterChange}
            />

            <input
                type="text"
                className="form-control"
                placeholder="Instance ID"
                name="instance_id"
                value={filters.instance_id}
                onChange={handleFilterChange}
            />

            <input
                type="text"
                className="form-control"
                placeholder="App Code"
                name="app_code"
                value={filters.app_code}
                onChange={handleFilterChange}
            />

            <input
                type="text"
                className="form-control"
                placeholder="Serial No"
                name="engine_serial_no"
                value={filters.engine_serial_no}
                onChange={handleFilterChange}
            />

            <input
                type="text"
                className="form-control"
                placeholder="Engineer"
                name="assigned_engineer"
                value={filters.assigned_engineer}
                onChange={handleFilterChange}
            />

            <select
                className="form-select"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
            >

                <option value="ALL">
                All Status
                </option>

                <option value="OPEN">
                OPEN
                </option>

                <option value="ASSIGNED">
                ASSIGNED
                </option>

                <option value="IN_PROGRESS">
                IN PROGRESS
                </option>

                <option value="RESOLVED">
                RESOLVED
                </option>

                <option value="CLOSED">
                CLOSED
                </option>

            </select>

            <select
                className="form-select"
                name="priority"
                value={filters.priority}
                onChange={handleFilterChange}
            >

                <option value="ALL">
                All Priority
                </option>

                <option value="LOW">
                Low
                </option>

                <option value="MEDIUM">
                Medium
                </option>

                <option value="HIGH">
                High
                </option>

                <option value="CRITICAL">
                Critical
                </option>

            </select>

            <select
                className="form-select"
                name="feedback_completed"
                value={filters.feedback_completed}
                onChange={handleFilterChange}
            >

                <option value="ALL">
                All Feedback
                </option>

                <option value="YES">
                Completed
                </option>

                <option value="NO">
                Pending
                </option>

            </select>

        </div>

        <div
          style={{
            marginTop: 16,
            display: "flex",
            gap: 10,
          }}
        >

          <button
  className="btn btn-primary"
  onClick={handleSearch}
>
  Search
</button>

<button
  className="btn btn-secondary"
  onClick={handleReset}
>
  Reset
</button>

<button
  className="btn btn-success"
  onClick={handleExportExcel}
>
  Export Excel
</button>

        </div>

      </div>

      {/* ===================================================== */}
      {/* TABLE */}
      {/* ===================================================== */}

      <div
        style={{

          background: "#fff",

          borderRadius: 14,

          overflow: "hidden",

          boxShadow:
            "0 2px 8px rgba(0,0,0,0.06)",

        }}
      >

        <div
          style={{
            overflowX: "auto",
          }}
        >

          <table
            className="table table-hover"
            style={{
              marginBottom: 0,
            }}
          >

            <thead
              className="table-light"
            >

              <tr>

                <th>Ticket No</th>

                <th>Customer</th>

                <th>Instance ID</th>

                <th>App Code</th>

                <th>Serial No</th>

                <th>Category</th>

                <th>Priority</th>

                <th>Engineer</th>

                <th>Status</th>

              </tr>

            </thead>

            <tbody>

              {loading && (

                <tr>

                  <td
                    colSpan="9"
                    className="text-center py-4"
                  >
                    Loading...
                  </td>

                </tr>

              )}

              {!loading &&
                rows.length === 0 && (

                <tr>

                  <td
                    colSpan="9"
                    className="text-center py-4"
                  >
                    No records found
                  </td>

                </tr>

              )}

              {!loading &&
                rows.map((row) => (

                <tr
                  key={row.id}
                >

                  <td>
                    {row.ticket_no}
                  </td>

                  <td>
                    {row.customer_name}
                  </td>

                  <td>
                    {row.instance_id}
                  </td>

                  <td>
                    {row.app_code}
                  </td>

                  <td>
                    {row.engine_serial_no}
                  </td>

                  <td>
                    {row.category}
                  </td>

                  <td>
                    {row.priority}
                  </td>

                  <td>
                    {
                      row.assigned_engineer
                    }
                  </td>

                  <td>
                    {
                      renderStatus(
                        row.status
                      )
                    }
                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>

      {/* ===================================================== */}
      {/* PAGINATION */}
      {/* ===================================================== */}

      <div
        style={{

          display: "flex",

          justifyContent:
            "space-between",

          alignItems: "center",

          marginTop: 16,

        }}
      >

        <div>

          Total:
          {" "}
          {total}

        </div>

        <div
          style={{

            display: "flex",

            gap: 8,

          }}
        >

          <button
            className="btn btn-outline-secondary btn-sm"

            disabled={
              filters.page <= 1
            }

            onClick={() =>
              setFilters({

                ...filters,

                page:
                  filters.page - 1,

              })
            }
          >
            Previous
          </button>

          <button
            className="btn btn-outline-secondary btn-sm"

            disabled={
              rows.length <
              filters.page_size
            }

            onClick={() =>
              setFilters({

                ...filters,

                page:
                  filters.page + 1,

              })
            }
          >
            Next
          </button>

        </div>

      </div>

    </div>

  );

}